import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BookmarkPlus, Check, Copy, Library, Loader2, MessageSquarePlus, Trash2 } from 'lucide-react'
import { AddToChatIcon } from './AddToChatIcon'
import { AgentNodeToolbar, type ToolbarAction } from './AgentNodeToolbar'
import { useNodeActions } from './useNodeActions'
import { useDuplicateMenuItems } from './useDuplicateMenuItems'
import { useSaveToLibrary } from './useSaveToLibrary'
import { createNadDebug } from '../../../../utils/debugLog'

const videoToolbarReferenceLog = createNadDebug('dw:video-node-toolbar-reference', {
  priority: true,
})

interface UploadMediaNodeToolbarProps {
  nodeId: string
  selected?: boolean
  dragging?: boolean
  /**
   * Media-type-specific actions. The video presentation places these between
   * Add to Chat and Library; the default presentation preserves the existing
   * upload-node order.
   *
   * This toolbar is shared by the image, audio and video upload nodes, so an
   * action that only makes sense for one of them (RFC-1115's "Annotate", which
   * needs an image path the audio node does not have) is owned by that node and
   * passed in rather than being conditionally built here.
   */
  extraActions?: ToolbarAction[]
  /** Use the fixed reference order and icons for uploaded video nodes. */
  presentation?: 'default' | 'video'
  /** Displayed node name (label or its fallback), used to name duplicates. */
  originalName: string
}

/** Render upload-node actions while preserving the selected media presentation. */
export function UploadMediaNodeToolbar({
  nodeId,
  selected,
  dragging,
  extraActions,
  presentation = 'default',
  originalName,
}: UploadMediaNodeToolbarProps) {
  const { t } = useTranslation('canvas')
  const {
    handleAddToChat,
    handleDuplicate,
    handleDelete,
  } = useNodeActions({ nodeId })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, originalName)
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId, selected })

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'add-to-chat',
        label: t('uploadMediaNodeToolbar.addToChat'),
        icon: presentation === 'video'
          ? <MessageSquarePlus className="h-4 w-4" />
          : <AddToChatIcon className="h-4 w-4" />,
        onClick: handleAddToChat,
      },
      ...(presentation === 'video' ? (extraActions ?? []) : []),
      {
        key: 'save-to-library',
        label: saveLabel,
        icon: saveState === 'loading'
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : saveState === 'success'
            ? <Check className="h-4 w-4 text-green-400" />
            : presentation === 'video'
              ? <Library className="h-4 w-4" />
              : <BookmarkPlus className="h-4 w-4" />,
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
      },
      ...(presentation === 'default' ? (extraActions ?? []) : []),
      {
        key: 'duplicate',
        label: t('uploadMediaNodeToolbar.duplicate'),
        icon: <Copy className="h-4 w-4" />,
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'remove',
        label: t('uploadMediaNodeToolbar.remove'),
        icon: <Trash2 className="h-4 w-4" />,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [t, presentation, extraActions, handleAddToChat, handleSaveToLibrary, saveState, saveLabel, duplicateMenuItems, handleDelete],
  )

  const visibleActionSignature = toolbarActions
    .filter((action) =>
      !action.hidden
      && !action.hideFromToolbar
      && (!action.menuItems || action.menuItems.some((item) => !item.hidden)),
    )
    .map((action) => action.key)
    .join('\u0000')

  useEffect(() => {
    if (presentation !== 'video') return
    videoToolbarReferenceLog({
      event: 'video-toolbar-layout-rendered',
      feature: 'video-node-toolbar-reference',
      trace_id: `video-node-toolbar-reference:${nodeId}`,
      node_id: nodeId,
      node_kind: 'video_upload',
      visible_action_order: visibleActionSignature.split('\u0000').join(' > '),
    })
  }, [nodeId, presentation, visibleActionSignature])

  return (
    <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />
  )
}
